import React, { useState, useRef, useLayoutEffect } from 'react'
import styled, { css } from 'styled-components'
import TableChart, { ColDefs } from 'components/visualization/Table'
import { TableConfigs } from 'components/visualization/Table/types'
// @ts-ignore
import { sys } from '@netbasequid/theme'
import { Theme } from 'features/ui/types'
import { useDisplayTheme } from 'hooks/useUiState'
import useWidthResizeThrottle from 'hooks/useWithResizeThrottle'

const rowStyle = {
  top: 10,
  bottom: 12,
  borderBottom: 1,
  paddingX: 32, // * padding left and right
  paddingLeftFirstColumn: 8,
  paddingRightLastColumn: 8,
  lineHeight: 22
}

interface StyledTableContainerProps {
  idealHeight: number
  defaultCellHeight: number
  isDisplayHead?: boolean
  idealWidth: number
  defaultCellWidth: number
  noHeaderOffset: number
  mode: Theme
}

const NonHeaderStyles = css<Pick<StyledTableContainerProps, 'noHeaderOffset'>>`
  thead {
    & > tr {
      /* workaround for Widget header (e.g. no header needs to be 10px, with header needs to be 4px) */
      /* Widget header always set to 4px and extra noHeaderOffset 6px when no header in table */
      height: ${({ noHeaderOffset }) => noHeaderOffset}px;
    }
  }
  ::-webkit-scrollbar-thumb:vertical {
    /* workaround to hide scrollbar of table header (e.g. 1px is the height of table header border and 6px for heder height) */
    border-top: ${({ noHeaderOffset }) => noHeaderOffset + 1}px solid transparent !important;
  }
`
const ThStyle = css<Pick<StyledTableContainerProps, 'mode' | 'idealWidth'>>`
  font-weight: 600;
  color: ${({ mode }) => sys[mode].text.primary};
  padding: 0 ${rowStyle.paddingX}px;
  & > div {
    & > div {
      /* set dynamic width to align table cell */
      max-width: ${({ idealWidth }) => idealWidth}px;
    }
  }
  &:first-child {
    padding-left: ${rowStyle.paddingLeftFirstColumn}px;
  }
  &:last-child {
    padding-right: ${rowStyle.paddingRightLastColumn}px;
  }
`
const TdStyle = css<Omit<StyledTableContainerProps, 'isDisplayHead'>>`
  padding: 0 ${rowStyle.paddingX}px;
  &:first-child {
    padding-left: ${rowStyle.paddingLeftFirstColumn}px;
  }
  &:last-child {
    padding-right: ${rowStyle.paddingRightLastColumn}px;
  }
  & > div {
    margin: ${rowStyle.top}px 0 ${rowStyle.bottom}px 0;

    min-height: ${({ defaultCellHeight }) => defaultCellHeight}px;
    min-width: ${({ defaultCellWidth }) => defaultCellWidth}px;
    box-sizing: border-box;
    /* border: 1px solid blue; */
    height: ${({ idealHeight }) => idealHeight}px;
    width: ${({ idealWidth }) => idealWidth}px;
  }
`
const StyledTableContainer = styled.div<StyledTableContainerProps>`
  /* border: 1px solid red; */
  height: 100%;
  & > div[data-testid='table-scrollbar'] {
    /* border: 1px solid green; */
    height: 100%;

    ${({ isDisplayHead }) => (isDisplayHead ? null : NonHeaderStyles)}

    & > table {
      /* border: 1px solid red; */
      height: 100%;
      th {
        ${ThStyle}
      }
      td {
        ${TdStyle}
      }
    }
  }
`

type TableProps<TRow, TContext, TField extends keyof TRow> = {
  /* displayed data */
  rowData: TRow[]
  /* data shared within the whole table */
  /* will pass an object containing props 'row' and 'theme' by default. */
  context?: TContext
  /* column definitions, define the behavior of each column */
  colDefs: ColDefs<TRow, TContext, TField>
  /* display table foot or not */
  isDisplayFoot: boolean
  /* includes general table configurations */
  configs?: TableConfigs<TRow>
  /* default cell height (also the min cell height, note: do not inclue margin need the raw height) */
  defaultCellHeight: number
  /* callback function when cell height change */
  onCellHeightChange?: (height: number) => void
  /* display table head or not */
  isDisplayHead?: boolean
  /* default cell width (also the min cell width, note: do not inclue margin need the raw width) */
  defaultCellWidth: number
  /* offset height of table header when isDisplayHead is false */
  noHeaderOffset?: number
}

const StrechTable = <TRow, TContext, TField extends keyof TRow>({
  rowData,
  isDisplayFoot,
  configs,
  context,
  colDefs,
  defaultCellHeight,
  onCellHeightChange,
  defaultCellWidth,
  isDisplayHead = true,
  noHeaderOffset = 0
}: TableProps<TRow, TContext, TField>) => {
  const containerEl = useRef<HTMLDivElement>(null)
  const [cellHeight, setCellHeight] = useState(defaultCellHeight)
  const [cellWidth, setCellWidth] = useState(defaultCellWidth)
  const theme = useDisplayTheme() as Theme

  const throttledWidth = useWidthResizeThrottle(
    containerEl,
    () => {
      // * reset to default to recalculate table cell
      setCellHeight(defaultCellHeight)
      setCellWidth(defaultCellWidth)
    },
    100
  )

  useLayoutEffect(() => {
    if (containerEl.current) {
      const tbodyEl = containerEl.current.getElementsByTagName('tbody')[0]
      const scrollContainerEl = containerEl.current.querySelector('[data-testid="table-scrollbar"]')

      const hasVerticalScroll =
        (scrollContainerEl?.clientHeight || 0) < (scrollContainerEl?.scrollHeight || 0)
      const hasHorizontalScroll =
        (scrollContainerEl?.clientWidth || 0) < (scrollContainerEl?.scrollWidth || 0)

      let newCellHeight = defaultCellHeight
      let newCellWidth = defaultCellWidth

      // * no vertical scroll means there is enough space for table cell height to strech
      if (!hasVerticalScroll) {
        const horizontalScrollHeight = hasHorizontalScroll ? 15 : 0

        // * calculate average height for every row ensure row are all the same height
        const idealRowHeight =
          (tbodyEl?.offsetHeight - horizontalScrollHeight) / rowData.length - rowStyle.borderBottom
        const idealCellHeight = idealRowHeight - (rowStyle.top + rowStyle.bottom)

        // * calculating lines is essential,
        // * e.g if idealCellHeight/lineHeight = 6.7, ellipsis will show in line 6 and the cell also can see the content of line 7
        // * we need to make sure control the lines between n ~ n.4
        const lines = Math.floor(idealCellHeight / rowStyle.lineHeight) + 0.4
        // * pick the cellHeight that is most suitable
        newCellHeight = Math.min(idealCellHeight, rowStyle.lineHeight * lines)
      }
      // * no horizontal scroll means there is enough space for table cell width to strech
      if (!hasHorizontalScroll) {
        const verticalScrollWidth = hasVerticalScroll ? 15 : 0
        const idealCellWidth =
          (tbodyEl?.offsetWidth -
            verticalScrollWidth -
            rowStyle.paddingLeftFirstColumn -
            rowStyle.paddingRightLastColumn -
            rowStyle.paddingX * 2 * (colDefs.length - 1)) /
          colDefs.length

        newCellWidth = Math.max(idealCellWidth, defaultCellWidth)
      }
      if (cellHeight !== newCellHeight) {
        setCellHeight(newCellHeight)
        onCellHeightChange?.(newCellHeight)
      }
      if (cellWidth !== newCellWidth) {
        setCellWidth(newCellWidth)
      }
    }
  }, [
    cellHeight,
    rowData.length,
    defaultCellHeight,
    onCellHeightChange,
    colDefs.length,
    defaultCellWidth,
    cellWidth,
    throttledWidth
  ])

  return (
    <StyledTableContainer
      ref={containerEl}
      idealHeight={cellHeight}
      defaultCellHeight={defaultCellHeight}
      idealWidth={cellWidth}
      defaultCellWidth={defaultCellWidth}
      isDisplayHead={isDisplayHead}
      noHeaderOffset={noHeaderOffset}
      mode={theme}>
      <TableChart
        rowData={rowData}
        colDefs={colDefs}
        isDisplayFoot={isDisplayFoot}
        configs={configs}
        context={context}
      />
    </StyledTableContainer>
  )
}

export default StrechTable
